import type { Presence } from '@cabgo/shared';
import { focusManager, type QueryClient, type QueryKey } from '@tanstack/react-query';

export const PRESENCE_REFRESH_MS = 15_000;

export const VISIBLE_PRESENCE_QUERY_OPTIONS = {
  refetchInterval: PRESENCE_REFRESH_MS,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: 'always',
} as const;

export interface PresenceUpdate {
  id: string;
  presence: Presence;
}

let nextPresenceRequestId = 0;
const latestPresenceByClient = new WeakMap<
  QueryClient,
  Map<string, { requestId: number; presence: Presence }>
>();
const latestCompletedPresenceRequestByClient = new WeakMap<QueryClient, number>();
const latestFilteredPresenceRequestByClient = new WeakMap<QueryClient, number>();

export function beginPresenceRequest(): number {
  nextPresenceRequestId += 1;
  return nextPresenceRequestId;
}

export class SupersededPresenceResponseError extends Error {
  constructor() {
    super('A newer presence response completed first');
  }
}

function sameQueryKey(left: QueryKey, right: QueryKey): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function candidatePresenceFilters(queryKey: QueryKey): { onlineOnly: boolean; freeOnly: boolean } | null {
  if (queryKey[0] !== 'candidates') return null;
  if (typeof queryKey[2] === 'boolean') {
    return { onlineOnly: queryKey[2], freeOnly: queryKey[3] === true };
  }
  if (typeof queryKey[3] === 'boolean') {
    return { onlineOnly: queryKey[3], freeOnly: queryKey[4] === true };
  }
  return null;
}

function updateCachedPresence(value: unknown, updates: ReadonlyMap<string, Presence>): unknown {
  if (!value || typeof value !== 'object') return value;
  const record = value as Record<string, unknown>;

  if (typeof record.id === 'string' && updates.has(record.id)) {
    const presence = updates.get(record.id);
    return record.presence === presence ? value : { ...record, presence };
  }

  if (!Array.isArray(record.data)) return value;
  let changed = false;
  const data = record.data.map((item: unknown) => {
    if (!item || typeof item !== 'object') return item;
    const row = item as Record<string, unknown>;
    if (row.driver && typeof row.driver === 'object') {
      const driver = updateCachedPresence(row.driver, updates);
      if (driver === row.driver) return item;
      changed = true;
      return { ...row, driver };
    }
    const updated = updateCachedPresence(item, updates);
    if (updated === item) return item;
    changed = true;
    return updated;
  });
  return changed ? { ...record, data } : value;
}

function updateFilteredCandidatePresence(
  value: unknown,
  updates: ReadonlyMap<string, Presence>,
  onlineOnly: boolean,
  _freeOnly: boolean,
): unknown {
  if (!value || typeof value !== 'object') return value;
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.data)) return value;
  let changed = false;
  let removed = 0;
  const data = record.data.flatMap((item: unknown) => {
    if (!item || typeof item !== 'object') return [item];
    const row = item as Record<string, unknown>;
    if (!row.driver || typeof row.driver !== 'object') return [item];
    const driver = row.driver as Record<string, unknown>;
    if (typeof driver.id !== 'string' || !updates.has(driver.id)) return [item];
    const presence = updates.get(driver.id)!;
    // `freeOnly` is based on schedule/active-trip availability, not presence.
    // A presence refresh may therefore only evict an OFFLINE row from an
    // `onlineOnly` result; it must not reinterpret BREAK/ON_DUTY as unavailable.
    if (onlineOnly && presence === 'OFFLINE') {
      changed = true;
      removed += 1;
      return [];
    }
    if (driver.presence === presence) return [item];
    changed = true;
    return [{ ...row, driver: { ...driver, presence } }];
  });
  if (!changed) return value;
  return {
    ...record,
    data,
    ...(typeof record.total === 'number' ? { total: Math.max(0, record.total - removed) } : {}),
  };
}

export function reconcilePresenceResponse<T>(
  queryClient: QueryClient,
  response: T,
  updates: readonly PresenceUpdate[],
  sourceQueryKey: QueryKey,
  requestId: number,
): T {
  const sourceFilters = candidatePresenceFilters(sourceQueryKey);
  const sourceUsesPresenceFilter = sourceFilters?.onlineOnly === true;
  const latestCompletedRequest = latestCompletedPresenceRequestByClient.get(queryClient) ?? 0;
  const latestFilteredRequest = latestFilteredPresenceRequestByClient.get(queryClient) ?? 0;
  if (
    (sourceUsesPresenceFilter && requestId < latestCompletedRequest) ||
    (!sourceUsesPresenceFilter && requestId < latestFilteredRequest)
  ) {
    throw new SupersededPresenceResponseError();
  }
  latestCompletedPresenceRequestByClient.set(queryClient, Math.max(latestCompletedRequest, requestId));
  if (sourceUsesPresenceFilter) {
    latestFilteredPresenceRequestByClient.set(queryClient, Math.max(latestFilteredRequest, requestId));
  }
  let latestByDriver = latestPresenceByClient.get(queryClient);
  if (!latestByDriver) {
    latestByDriver = new Map();
    latestPresenceByClient.set(queryClient, latestByDriver);
  }
  const effectiveUpdates = updates.map((update) => {
    const latest = latestByDriver.get(update.id);
    if (latest && latest.requestId > requestId) return { id: update.id, presence: latest.presence };
    latestByDriver.set(update.id, { requestId, presence: update.presence });
    return update;
  });
  const byId = new Map(effectiveUpdates.map(({ id, presence }) => [id, presence]));

  for (const prefix of ['drivers', 'driver', 'candidates']) {
    for (const [queryKey, value] of queryClient.getQueriesData({ queryKey: [prefix] })) {
      if (sameQueryKey(queryKey, sourceQueryKey)) continue;
      const targetFilters = candidatePresenceFilters(queryKey);
      const updated = targetFilters?.onlineOnly
        ? updateFilteredCandidatePresence(value, byId, targetFilters.onlineOnly, targetFilters.freeOnly)
        : updateCachedPresence(value, byId);
      if (updated !== value) {
        queryClient.getQueryCache().find({ queryKey, exact: true })?.setState({ data: updated });
      }
    }
  }

  if (sourceQueryKey[0] === 'candidates') {
    void queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === 'candidates' && !sameQueryKey(query.queryKey, sourceQueryKey),
      refetchType: 'none',
    });
    if (sourceUsesPresenceFilter) {
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'drivers' || query.queryKey[0] === 'driver',
        refetchType: 'active',
      });
    }
  } else if (updates.length > 0) {
    void queryClient.invalidateQueries({ queryKey: ['candidates'], refetchType: 'active' });
  }

  const reconciledResponse = sourceUsesPresenceFilter
    ? updateFilteredCandidatePresence(response, byId, sourceFilters.onlineOnly, sourceFilters.freeOnly)
    : updateCachedPresence(response, byId);

  return reconciledResponse as T;
}

export function configureQueryFocusEvents() {
  focusManager.setEventListener((onFocus) => {
    let suppressNextFocus = false;
    let suppressionTimer: number | undefined;

    const handleVisibilityChange = () => {
      const visible = document.visibilityState !== 'hidden';
      onFocus(visible);
      if (visible) {
        suppressNextFocus = true;
        window.clearTimeout(suppressionTimer);
        suppressionTimer = window.setTimeout(() => {
          suppressNextFocus = false;
        });
      }
    };
    const handleWindowFocus = () => {
      if (document.visibilityState !== 'hidden' && !suppressNextFocus) onFocus();
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.clearTimeout(suppressionTimer);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  });
}
